import { API_BASE_URL } from '../utils/constants';

class ApiService {
    async request(endpoint, options = {}) {
        const token = localStorage.getItem('token');

        // Check if the request body is FormData
        const isFormData = options.body instanceof FormData;

        const config = {
            ...options,
            headers: {
                // Only set Content-Type for JSON requests, not for FormData
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
        };

        // Add timeout support
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), options.timeout || 120000);

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...config,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Don't automatically redirect on 401 for login endpoint
        if (response.status === 401 && !endpoint.includes('/auth/login')) {
            localStorage.removeItem('token');
            window.location.href = '/login';
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({
                detail: `HTTP ${response.status}: ${response.statusText}`
            }));

            let message = 'Request failed';
            if (typeof error.detail === 'string') {
                message = error.detail;
            } else if (Array.isArray(error.detail)) {
                message = error.detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
            } else if (error.message) {
                message = error.message;
            }

            const apiError = new Error(message);
            apiError.status = response.status;
            apiError.statusText = response.statusText;
            apiError.errorData = error;
            throw apiError;
        }

        return response.json();
    }

    get(endpoint) {
        return this.request(endpoint, { method: 'GET' });
    }

    post(endpoint, data) {
        // Check if data is FormData
        const isFormData = data instanceof FormData;

        return this.request(endpoint, {
            method: 'POST',
            body: isFormData ? data : JSON.stringify(data),
        });
    }

    put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }
}

export const api = new ApiService();